import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, CardContent, Stack, InputAdornment, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import tipoFormularioService, { TipoFormularioPagedResult, TIPO_FORMULARIO_MAX_PAGE_SIZE } from '../../services/tipoFormularioService';

/** Muestra Si/No según la convención de cada flag del maestro. */
const siNoStockNegativo = (v: number | null | undefined) =>
  v == null ? '-' : Number(v) === 1 ? 'Si' : 'No';

const siNoInfoCons = (v: number | null | undefined) =>
  v == null ? '-' : Number(v) === 1 ? 'Si' : 'No';

/** Convención maestro: 0 = Sí descuenta, 1 = No. */
const siNoDescuentaStock = (v: number | null | undefined) =>
  v == null ? '-' : Number(v) === 0 ? 'Si' : 'No';

const TipoFormulario: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tiposData, setTiposData] = useState<TipoFormularioPagedResult>({
    items: [], totalCount: 0, pageNumber: 1, pageSize: 10, totalPages: 0
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(Math.min(10, TIPO_FORMULARIO_MAX_PAGE_SIZE));
  const [searchTerm, setSearchTerm] = useState('');
  const [_loading, setLoading] = useState(false);

  const fetchTipoFormularios = async (pageNumber: number, pageSize: number, search?: string) => {
    try {
      setLoading(true);
      const response = await tipoFormularioService.getTipoFormularios({
        pageNumber,
        pageSize,
        descripcion: search?.trim() || undefined
      });
      setTiposData(response);
    } catch (error: any) {
      console.error('Error al cargar tipos de formulario:', error);
      const msg = error?.response?.data?.error || error?.response?.data?.message || 'Error al cargar tipos de formulario';
      enqueueSnackbar(msg, { variant: 'error' });
      setTiposData({ items: [], totalCount: 0, pageNumber: 1, pageSize, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchTipoFormularios(page + 1, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = Math.min(parseInt(event.target.value, 10), TIPO_FORMULARIO_MAX_PAGE_SIZE);
    setRowsPerPage(val);
    setPage(0);
  };

  const handleDelete = async (nroTipoFormulario: string) => {
    if (window.confirm('¿Está seguro de eliminar este tipo de formulario?')) {
      try {
        await tipoFormularioService.deleteTipoFormulario(nroTipoFormulario);
        enqueueSnackbar('Tipo de formulario eliminado exitosamente', { variant: 'success' });
        fetchTipoFormularios(page + 1, rowsPerPage, searchTerm);
      } catch (error: any) {
        console.error('Error al eliminar tipo de formulario:', error);
        enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar tipo de formulario', { variant: 'error' });
      }
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Tipos de Formulario
            </Typography>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/sistema/tipo-formulario/nuevo')}>
              Nuevo Tipo
            </Button>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {tiposData.items.map((tipo) => (
                <Card key={tipo.nroTipoFormulario} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {tipo.nroTipoFormulario} - {tipo.descripcion}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Área: {tipo.area} · Stock Neg: {siNoStockNegativo(tipo.stockNegativo)} · InfoCons: {siNoInfoCons(tipo.infoCons)} · Desc. Stock: {siNoDescuentaStock(tipo.descuentaStock)}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => navigate(`/sistema/tipo-formulario/${tipo.nroTipoFormulario}`)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(tipo.nroTipoFormulario)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>N° Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Área</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Libro IVA</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Stock Neg.</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Info Cons</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Desc. Stock</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tiposData.items.map((tipo) => (
                    <TableRow key={tipo.nroTipoFormulario} hover>
                      <TableCell>{tipo.nroTipoFormulario}</TableCell>
                      <TableCell>{tipo.descripcion}</TableCell>
                      <TableCell>{tipo.area}</TableCell>
                      <TableCell>{tipo.utilizaLibroIva}</TableCell>
                      <TableCell>{siNoStockNegativo(tipo.stockNegativo)}</TableCell>
                      <TableCell>{siNoInfoCons(tipo.infoCons)}</TableCell>
                      <TableCell>{siNoDescuentaStock(tipo.descuentaStock)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => navigate(`/sistema/tipo-formulario/${tipo.nroTipoFormulario}`)} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleDelete(tipo.nroTipoFormulario)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={tiposData.totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 20]}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TipoFormulario;
